import React, { useRef, useState } from 'react';
import { Dayjs } from 'dayjs';
import { SensorDescriptor } from '@dnd-kit/core';
import { SegmentedAppointment } from '../segments';
import { TechLane } from '../useSchedulingData';
import { ScheduleException } from '../../../services/scheduleApi';
import { ALL_LANE_KEY, DayGrid, UNASSIGNED_LANE_KEY } from './DayGrid';
import { DropIntent } from './WeekGrid';

export interface MobileDayProps {
  date: Dayjs;
  appts: SegmentedAppointment[];
  techs: TechLane[];
  exceptions: ScheduleException[];
  onOpen: (id: number) => void;
  onDrop?: (e: DropIntent) => void;
  sensors?: SensorDescriptor<any>[];
  onEditBlock?: (ex: ScheduleException) => void;
  onCreateBlock?: (techId: number | undefined, startLocal: string) => void;
}

// Same threshold MobileWeek uses for its day-swipe gesture — a shorter drag
// reads as a scroll/tap, not a deliberate tech-change swipe.
const SWIPE_THRESHOLD = 60;

/** SP-1 Task 9: mobile Day = one tech's column full-width. All N tech lanes
 *  side-by-side (desktop DayGrid) don't fit at 390px, so mobile gets a tech
 *  tab bar (+ Unassigned) above a DayGrid narrowed to just the selected lane
 *  via `onlyLaneKey` — appts/exceptions still classify against the full
 *  `techs` list (so color + the Unassigned fallback for unknown techs stay
 *  correct), only the rendered lane is restricted. Swipe left/right over the
 *  grid also cycles techs, mirroring MobileWeek's day-swipe. */
export function MobileDay({
  date,
  appts,
  techs,
  exceptions,
  onOpen,
  onDrop,
  sensors,
  onEditBlock,
  onCreateBlock,
}: MobileDayProps) {
  // DayGrid collapses to its own single ALL_LANE_KEY fallback lane when no
  // techs are provided at all (rather than an 'unassigned' lane) — target
  // whichever key DayGrid will actually render so the "Unassigned" tab still
  // shows content in that edge case (no active technicians on the shop).
  const unassignedTargetKey = techs.length ? UNASSIGNED_LANE_KEY : ALL_LANE_KEY;
  // Selection order for both the tab bar and swipe-cycling: every active tech,
  // then Unassigned last.
  const laneKeys = [...techs.map((t) => String(t.id)), unassignedTargetKey];
  const [selectedKey, setSelectedKey] = useState<string>(laneKeys[0] ?? unassignedTargetKey);
  // A tech list that loads in after mount (or a selection that's no longer
  // valid, e.g. the previously-selected tech went inactive) — fall back to
  // the first available lane rather than rendering an empty selection.
  const activeKey = laneKeys.includes(selectedKey) ? selectedKey : laneKeys[0];

  const gridAreaRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchStartScrollLeft = useRef<number | null>(null);
  // Same drop-vs-swipe disambiguation MobileWeek uses: a committed drag-drop
  // during this touch gesture is an edit, not a request to change techs.
  const dropHandledRef = useRef(false);
  const handleDrop = (e: DropIntent) => {
    dropHandledRef.current = true;
    onDrop?.(e);
  };

  const selectByOffset = (offset: 1 | -1) => {
    const idx = laneKeys.indexOf(activeKey);
    const nextIdx = (idx + offset + laneKeys.length) % laneKeys.length;
    setSelectedKey(laneKeys[nextIdx]);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY ?? 0;
    dropHandledRef.current = false;
    const laneRow = gridAreaRef.current?.querySelector<HTMLElement>('[data-testid="day-grid"]');
    touchStartScrollLeft.current = laneRow ? laneRow.scrollLeft : null;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartX.current;
    const startY = touchStartY.current;
    const startScrollLeft = touchStartScrollLeft.current;
    const dropHandled = dropHandledRef.current;
    touchStartX.current = null;
    touchStartY.current = null;
    touchStartScrollLeft.current = null;
    dropHandledRef.current = false;

    if (startX === null) return;
    if (dropHandled) return;

    const laneRow = gridAreaRef.current?.querySelector<HTMLElement>('[data-testid="day-grid"]');
    if (laneRow && startScrollLeft !== null && laneRow.scrollLeft !== startScrollLeft) return;

    const dx = e.changedTouches[0].clientX - startX;
    const dy = startY !== null ? (e.changedTouches[0].clientY ?? 0) - startY : 0;
    const isTechSwipe = Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > 1.5 * Math.abs(dy);
    if (isTechSwipe) {
      selectByOffset(dx < 0 ? 1 : -1);
    }
  };

  return (
    <div data-testid="mobile-day">
      <div
        data-testid="mobile-day-tech-selector"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          display: 'flex',
          gap: 4,
          padding: '8px 4px',
          overflowX: 'auto',
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        {techs.map((tech) => {
          const key = String(tech.id);
          const isSelected = key === activeKey;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={isSelected}
              data-testid={`mobile-day-tab-${key}`}
              onClick={() => setSelectedKey(key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flex: '1 0 auto',
                padding: '8px 14px',
                borderRadius: 8,
                border: isSelected ? '2px solid #2563eb' : '1px solid #e5e7eb',
                backgroundColor: isSelected ? '#eff6ff' : '#fff',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {tech.calendar_color && (
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: tech.calendar_color,
                    flexShrink: 0,
                  }}
                />
              )}
              {tech.name}
            </button>
          );
        })}
        <button
          type="button"
          aria-pressed={activeKey === unassignedTargetKey}
          data-testid="mobile-day-tab-unassigned"
          onClick={() => setSelectedKey(unassignedTargetKey)}
          style={{
            flex: '1 0 auto',
            padding: '8px 14px',
            borderRadius: 8,
            border: activeKey === unassignedTargetKey ? '2px solid #2563eb' : '1px solid #e5e7eb',
            backgroundColor: activeKey === unassignedTargetKey ? '#eff6ff' : '#fff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Unassigned
        </button>
      </div>
      <div
        ref={gridAreaRef}
        data-testid="mobile-day-grid-area"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <DayGrid
          date={date}
          appts={appts}
          techs={techs}
          exceptions={exceptions}
          onOpen={onOpen}
          onDrop={handleDrop}
          sensors={sensors}
          onEditBlock={onEditBlock}
          onCreateBlock={onCreateBlock}
          onlyLaneKey={activeKey}
        />
      </div>
    </div>
  );
}

export default MobileDay;
